import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import { titleize } from 'underscore.string';
import Badge from 'components/v2/Badge';
import Pagination from 'components/v2/Pagination';
import InfoIcon from 'components/v2/icons/InfoIcon';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import ExternalLinkIcon from '../../../v2/icons/ExternalLinkIcon';
import LoadingCircle from '../../../ui/LoadingCircle';
import DataPoller from '../../../ui/DataPoller';

const PER_PAGE = 20;

export default function ShipmentListModal({
  showModal,
  shipments,
  shipmentsLoading,
  shipmentsTotal,
  onFilterChange,
  onSave,
  onClose,
  dialogClassName,
}) {
  const { t } = useTranslation();
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [sortingField, setSortingField] = useState(null);
  const [sortingDesc, setSortingDesc] = useState(false);
  const [page, setPage] = useState(1);
  const [activePodPreview, setActivePodPreview] = useState();

  useEffect(() => {
    fetchData();
  }, []);

  function fetchData(disableLoader = false) {
    onFilterChange(
      {
        sort: sortingField,
        sort_dir: sortingDesc ? 'DESC' : 'ASC',
        page,
        per_page: PER_PAGE,
      },
      false,
      [],
      disableLoader,
    );
  }

  function sortByField(field) {
    let newSortingField = field;
    let descending = false;

    // Rotate sorting (asc -> desc -> none)
    if (field === sortingField) {
      if (sortingDesc) {
        newSortingField = null;
      } else {
        descending = true;
      }
    }

    setSortingField(newSortingField);
    setSortingDesc(descending);

    onFilterChange({
      sort: newSortingField,
      sort_dir: descending ? 'DESC' : 'ASC',
      page: 1,
      per_page: PER_PAGE,
    });
  }

  function handlePageClick(
    newPage,
    filterOutSelected = false,
    selectedShipments = [],
  ) {
    setPage(newPage);
    onFilterChange(
      {
        sort: sortingField,
        sort_dir: sortingDesc ? 'DESC' : 'ASC',
        page: newPage,
        per_page: PER_PAGE,
      },
      filterOutSelected,
      selectedShipments,
    );
  }

  function handleSelectAll() {
    if (selectedShipments.length === shipments.length) {
      setSelectedShipments([]);
    } else {
      const isCCBATEnabled = featureEnabled(CONFIGURED_FEATURES.CCBAT);
      setSelectedShipments(
        shipments
          .filter((shipment) => {
            const { documents, byPassDocumentCheck } = shipment;
            return isCCBATEnabled
              ? byPassDocumentCheck
                ? byPassDocumentCheck
                : documents?.length
              : shipment.is_pod_valid;
          })
          .map((shipment) => shipment._id),
      );
    }
  }

  function handleSelect(id) {
    if (selectedShipments.includes(id)) {
      setSelectedShipments((prev) =>
        prev.filter((shipment) => shipment !== id),
      );
    } else {
      setSelectedShipments((prev) => [...prev, id]);
    }
  }

  function handlePODOpen(signedUrl) {
    window.open(signedUrl, '_blank');
  }

  async function handlePODPreviewOpen(pod) {
    setActivePodPreview(pod);
  }

  function handleSave() {
    onSave(selectedShipments);
    handlePageClick(1, true, selectedShipments);
  }

  function disableApproveButton() {
    return selectedShipments.length === 0;
  }

  const useDocumentIndexes = () => {
    let pod = 0;
    let eir = 0;
    let interchangeIn = 0;
    let interchangeout = 0;

    const getPod = () => {
      pod++;
      return pod;
    };
    const getEir = () => {
      eir++;
      return eir;
    };
    const getInterchangeIn = () => {
      interchangeIn++;
      return interchangeIn;
    };
    const getInterchangeOut = () => {
      interchangeout++;
      return interchangeout;
    };

    return {
      getPod,
      getEir,
      getInterchangeIn,
      getInterchangeOut,
    };
  };

  function getBody() {
    const loadingText = `${t('LOADING')}`;
    const loadedShipments = shipmentsLoading ? [] : shipments;

    return (
      <div className="block" data-testid="shipment-modal">
        <div data-testid="div-53114b20" className="max-h-96 overflow-y-auto">
          <table data-testid="table-d42918e6" className="default-table">
            <thead data-testid="thead-05607832">
              <tr data-testid="tr-c662c8a3">
                <th data-testid="th-aecca39b">
                  <div
                    data-testid="div-f2cb6d6d"
                    className="flex gap-2 cursor-pointer"
                    onClick={() => sortByField('shipment_id')}
                  >
                    <span data-testid="span-9b554fec">
                      {t('SHIPMENT', { count: 1 })}
                    </span>
                    <span data-testid="span-bc1d1fb9">
                      {sortingField &&
                        (sortingDesc ? <>&uarr;</> : <>&darr;</>)}
                    </span>
                  </div>
                </th>
                <th data-testid="th-471ad539" colSpan={2}>
                  {t('CONTAINER_NUMBER')}
                </th>
                <th data-testid="th-bd3db928" colSpan={2}>
                  {t('DOCUMENT')}
                </th>
                <th data-testid="th-3030f7e5">
                  <CargomaticInput
                    dataTestId="CargomaticInput-bc10b493"
                    id="select_all"
                    type="checkbox"
                    inputClassNames="border-gray-400"
                    label={titleize(t('ALL'))}
                    info={null}
                    value={shipments
                      .map((shipment) => shipment._id)
                      .every((_id) => selectedShipments.includes(_id))}
                    onChange={handleSelectAll}
                  />
                </th>
              </tr>
            </thead>
            <tbody data-testid="tbody-f89e87ba">
              {shipmentsLoading && (
                <div data-testid="div-b7b09828" className="p-4">
                  <LoadingCircle
                    dataTestId="LoadingCircle-2ba76df6"
                    text={loadingText}
                  />
                </div>
              )}
              {loadedShipments.map((shipment, index) => {
                const confidenceByDocId = new Map();
                shipment.confidence_level?.forEach((cL) => {
                  confidenceByDocId.set(cL.document_id, cL);
                });

                let equipments = shipment.reference_numbers
                  .filter((el) => el.name === 'equipment_id')
                  .map((el) => el.optimized);

                const documentIndexes = useDocumentIndexes();

                const renderDocumentTypeWithLink = (doc) => {
                  let index;
                  let messageKey;

                  switch (doc.type) {
                    case 'proof_of_delivery':
                      index = documentIndexes.getPod();
                      messageKey = 'POD_INDEX';
                      break;
                    case 'eir_ticket':
                      index = documentIndexes.getEir();
                      messageKey = 'EIR_INDEX';
                      break;
                    case 'interchange_in':
                      index = documentIndexes.getInterchangeIn();
                      messageKey = 'INTERCHANGE_IN_INDEX';
                      break;
                    case 'interchange_out':
                      index = documentIndexes.getInterchangeOut();
                      messageKey = 'INTERCHANGE_OUT_INDEX';
                      break;
                    default:
                      index = null;
                      messageKey = '';
                      break;
                  }
                  return t(messageKey, { index });
                };
                const isCCBATEnabled = featureEnabled(
                  CONFIGURED_FEATURES.CCBAT,
                );
                const { documents, byPassDocumentCheck } = shipment;
                let enableCheckbox = isCCBATEnabled
                  ? byPassDocumentCheck
                    ? !byPassDocumentCheck
                    : !documents?.length
                  : !shipment.is_pod_valid;

                return (
                  <tr data-testid={`tr-3e1bd8b7-${index}`} key={shipment._id}>
                    <td data-testid={`td-dd0327f4-${index}`} className="py-1">
                      <NavLink
                        target="_blank"
                        to={`/shipments/${shipment._id}`}
                      >
                        <span data-testid={`span-2a2de47c-${index}`}>
                          {shipment.shipment_id}
                        </span>
                      </NavLink>
                    </td>
                    <td
                      data-testid={`td-c3c7c617-${index}`}
                      className="py-1"
                      colSpan={2}
                    >
                      {equipments.join(', ')}
                    </td>
                    <td
                      data-testid={`td-27c557e7-${index}`}
                      className="py-1"
                      colSpan={2}
                    >
                      {shipment.documents.map((doc, index) => {
                        return (
                          <div
                            data-testid={`div-d8c1041d-${index}`}
                            className="flex items-center"
                          >
                            <Badge
                              dataTestId={`Badge-18d8e68e-${index}`}
                              className="w-48 cursor-pointer flex justify-between items-center mb-1"
                              color="green"
                              key={doc.id}
                              onClick={() => {
                                handlePODPreviewOpen(doc);
                              }}
                            >
                              <span
                                data-testid={`span-4669342d-${index}`}
                                className="mr-1"
                              >
                                <InfoIcon />
                              </span>
                              {renderDocumentTypeWithLink(doc)}
                            </Badge>
                            <a
                              data-testid={`a-5c7fc75a-${index}`}
                              href="#"
                              className="cursor-pointer"
                              aria-label="link"
                              onClick={() => {
                                handlePODOpen(doc.signedUrl);
                              }}
                            >
                              <ExternalLinkIcon className="w-4 h-4 flex-shrink-0" />
                            </a>
                          </div>
                        );
                      })}
                    </td>
                    <td
                      data-testid={`td-7570cb14-${index}`}
                      className="py-1"
                      colSpan={2}
                    >
                      <CargomaticInput
                        dataTestId={`CargomaticInput-e3cb4efb-${index}`}
                        id={`select_${index}`}
                        aria-label="checkbox"
                        type="checkbox"
                        inputClassNames="border-gray-400"
                        label={null}
                        info={null}
                        value={selectedShipments.includes(shipment._id)}
                        onChange={() => handleSelect(shipment._id)}
                        disabled={enableCheckbox}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DocumentPreviewModal
          onClose={() => setActivePodPreview(null)}
          document={activePodPreview}
        />
      </div>
    );
  }

  function getFooter() {
    return (
      <div data-testid="div-711dcd0c">
        {shipmentsTotal > 0 && (
          <Pagination
            dataTestId="Pagination-69807257"
            className=""
            perPage={PER_PAGE}
            page={page}
            pages={Math.ceil(shipmentsTotal / PER_PAGE)}
            total={shipmentsTotal}
            onPageClick={handlePageClick}
          />
        )}
        <button
          data-testid="button-5ef752ac"
          className="button button-secondary"
          type="button"
          onClick={onClose}
        >
          {titleize(t('CANCEL'))}
        </button>
        <button
          data-testid="button-4116953c"
          className="button button-green"
          disabled={disableApproveButton()}
          onClick={handleSave}
        >
          {titleize(t('APPROVE'))}
        </button>
      </div>
    );
  }

  function renderTitleBar() {
    return (
      <div
        data-testid="div-7526a7f1"
        className="flex justify-between items-center"
      >
        <div data-testid="div-19412587" className="modal-title-bar">
          <div data-testid="div-d971a041" className="modal-title-bar-text">
            {t('SHIPMENT', { count: 2 })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <CargomaticModal
        dataTestId="CargomaticModal-129d8695"
        title={renderTitleBar()}
        dialogClassName={dialogClassName}
        style={{ top: '10%' }}
        size="large"
        showCloseButton={false}
        show={showModal}
        onHide={onClose}
        body={getBody()}
        footer={getFooter()}
      />{' '}
      <DataPoller
        dataTestId="DataPoller-998c3752"
        frequency={20000}
        action={() => fetchData(true)}
      />
    </>
  );
}

ShipmentListModal.propTypes = {
  showModal: PropTypes.bool,
  shipments: PropTypes.array,
  shipmentsLoading: PropTypes.bool,
  shipmentsTotal: PropTypes.number,
  onFilterChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
